import { FunctionComponent, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RoutesList } from '@cycle-hangars/routes/Routes';
import { MapTemplate } from '@templates';
import {
  AssetTypeConfig,
  CycleHangarsEstatesFeatureAttributes,
  CycleHangarsFeatureAttributes,
  emptyServiceLocationDetails,
  ErrorMessages,
} from '@types';
import {
  addressResponseType,
  convertAddressToServiceLocation,
  convertServiceLocationToAddress,
  errorFormatter,
  issueLocationValidation,
  stringFieldSchema,
} from '@utils';
import { useCycleStore } from '@cycle-hangars/store/store';
import { SelectedLocationTextContent } from './SelectedLocationTextContent';
import { ZodError } from 'zod';
import { LatLngTuple } from 'leaflet';

export const IssueLocation: FunctionComponent = () => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState<ErrorMessages[]>([]);
  const errorPath = `issue-Location`;
  const pageTitle = 'Where is the cycle hangar located?';
  const subText =
    'Select a cycle hanger location on the map. Each location is marked by a blue square.';
  const {
    serviceLocationDetails,
    serviceAssetDetails,
    updateServiceLocationDetails,
    updateServiceAssetDetails,
  } = useCycleStore();

  const { latitude, longitude } = serviceLocationDetails;

  const defaultLocation =
    latitude && longitude ? ([latitude, longitude] as LatLngTuple) : undefined;

  const handleOnPreviousClick = () => {
    navigate(RoutesList.DETAILS);
  };

  const handleOnNextClick = () => {
    setErrorMessages([]);
    const assetID =
      (serviceAssetDetails as CycleHangarsFeatureAttributes)?.CH_Ref ||
      (serviceAssetDetails as CycleHangarsEstatesFeatureAttributes)?.Unit_ID ||
      '';
    try {
      issueLocationValidation.parse({
        locationIssue: serviceLocationDetails.UPRN ?? '',
      });
      stringFieldSchema('assetID', 'Select a blue square on the map').parse({
        assetID,
      });
      navigate(RoutesList.ISSUE_SELECTION);
    } catch (error) {
      const { errorMessages } = errorFormatter(error as ZodError);
      setErrorMessages([
        { path: errorPath, message: errorMessages[0].message },
      ]);
    }
  };

  const buttonPrevious = {
    label: 'Previous',
    onClick: handleOnPreviousClick,
  };

  const buttonNext = {
    label: 'Next',
    onClick: handleOnNextClick,
  };

  const handleAddressChange = async (address: addressResponseType) => {
    setErrorMessages([]);
    if (Object.keys(address).length === 0) {
      // Clears address and asset details when user clears address from search bar
      updateServiceLocationDetails(emptyServiceLocationDetails);
      updateServiceAssetDetails(null);
      return;
    } else {
      updateServiceLocationDetails(convertAddressToServiceLocation(address));
      address.asset
        ? updateServiceAssetDetails(
            address.asset as
              | CycleHangarsEstatesFeatureAttributes
              | CycleHangarsFeatureAttributes,
          )
        : updateServiceAssetDetails(null);
    }
  };

  const cycleHangarAsset: AssetTypeConfig = {
    asset: 'CycleHangars',
    icon: {
      shape: 'square',
      size: 48,
    },
    color: '#0335FF',
    onAdd(geojson, marker) {
      const markerElement = marker.getElement();
      const description = `Cycle Hangar ID: ${geojson.properties.CH_Ref}`;
      if (markerElement) {
        markerElement.setAttribute('alt', description);
      }
    },
  };

  const CycleHangarsEstates: AssetTypeConfig = {
    asset: 'CycleHangarsEstates',
    icon: {
      shape: 'square',
      size: 48,
    },
    color: '#0335FF',
    onAdd(geojson, marker) {
      const markerElement = marker.getElement();
      const description = `Cycle Hangar ID: ${geojson.properties.Unit_ID}`;
      if (markerElement) {
        markerElement.setAttribute('alt', description);
      }
    },
  };

  return (
    <MapTemplate
      id={errorPath}
      labelText={pageTitle}
      assetType={[cycleHangarAsset, CycleHangarsEstates]}
      displayMapAssets={true}
      subText={subText}
      errorPath={errorPath}
      errorMessages={errorMessages}
      defaultLocation={defaultLocation}
      onChangeAddress={handleAddressChange}
      selectedAddress={convertServiceLocationToAddress(serviceLocationDetails)}
      customAddressContent={<SelectedLocationTextContent />}
      buttonNext={buttonNext}
      buttonPrevious={buttonPrevious}
    />
  );
};
